import { useState, useEffect, useRef, useMemo } from 'react';
import devDebug from '../Helpers/debug';

export function useAllStoresHydrated(storeHooks) {
    // Memoize the initial hydration states to avoid re-calculating on every render
    const initialHydrationStates = useMemo(() => {
        return storeHooks.map((storeHook, index) => {
            if (!storeHook.persist) {
                devDebug(
                    `Store at index ${index} (potentially ${storeHook.name || 'UnnamedStore'}) does not seem to use persist middleware. It will be considered hydrated.`
                );
                return true; // Assume non-persisted stores are "hydrated"
            }
            try {
                return storeHook.persist.hasHydrated();
            } catch (error) {
                devDebug(
                    `Error accessing persist API for store at index ${index} (potentially ${storeHook.name || 'UnnamedStore'}). Ensure it's a valid Zustand store with persist middleware.`,
                    error
                );
                return true; // Treat as hydrated to prevent blocking if misconfigured
            }
        });
    }, [storeHooks]); // Only re-calculate if the array of storeHooks itself changes

    const [allHydrated, setAllHydrated] = useState(
        initialHydrationStates.every(Boolean)
    );

    // Use a ref to track the hydration status of individual stores
    // This prevents re-triggering the effect for each minor status update if not needed
    const individualHydrationStatus = useRef([...initialHydrationStates]);

    useEffect(() => {
        if (allHydrated) {
            return; // All already hydrated, nothing more to do
        }

        let stillWaitingCount = individualHydrationStatus.current.filter(status => !status).length;
        if (stillWaitingCount === 0) { // Should have been caught by initial allHydrated state
            setAllHydrated(true);
            return;
        }

        const unsubs = storeHooks.map((storeHook, index) => {
            // If this store was already hydrated initially or doesn't use persist, skip
            if (individualHydrationStatus.current[index] || !storeHook.persist) {
                return () => { }; // No-op unsubscribe
            }

            const onFinish = () => {
                if (!individualHydrationStatus.current[index]) {
                    individualHydrationStatus.current[index] = true;
                    stillWaitingCount--;
                    devDebug(`Store ${storeHook.name || index} hydrated. Remaining: ${stillWaitingCount}`);
                    if (stillWaitingCount === 0) {
                        devDebug('All stores have finished hydration.');
                        setAllHydrated(true);
                    }
                }
            };

            // Check again, hydration might have finished between effect start and subscription
            if (storeHook.persist.hasHydrated()) {
                onFinish();
                return () => { };
            }

            const unsub = storeHook.persist.onFinishHydration(onFinish);
            return unsub;
        });

        // Cleanup: unsubscribe from all stores
        return () => {
            unsubs.forEach(unsub => unsub());
        };
    }, [storeHooks, allHydrated]); // Re-run if storeHooks array changes or if allHydrated status changes

    return allHydrated;
}